import { cleanDiagnostics, type DiagnosticFields } from "./taskDiagnostics";

import { createWorkspaceCache } from "./workspaceCache";
import { apiErrorMessage } from "./apiErrorMessage";
// New page load = new volatile editing session; never store this in localStorage.
let editingSession = crypto.randomUUID();
let sessionGeneration = 0;
export function clearWorkspaceSession() {
  editingSession = crypto.randomUUID();
  sessionGeneration++;
  workspaceCache.invalidate();
  try {
    for (const storage of [localStorage, sessionStorage]) {
      for (const key of Object.keys(storage)) {
        if (key.startsWith("aclip-") || key.startsWith("aclip:")) {
          // Keep deployment metadata: removing it makes a reload look like a
          // database reset and discards the same account's open preview state.
          if (!["aclip-script-view", "aclip-asset-view", "aclip-data-epoch"].includes(key))
            storage.removeItem(key);
        }
      }
    }
  } catch {
    /* Cookie authentication also works when browser storage is disabled. */
  }
}

const workspaceCache = createWorkspaceCache((path) =>
  api(path, { signal: AbortSignal.timeout(15_000) }),
);
// Compatibility exports: callers do not own cache lifetime or invalidation.
export const workspaceSnapshot = workspaceCache.snapshot;
export const workspaceRead = workspaceCache.read;

export class ApiError extends Error {
  code: string;
  status: number;
  diagnostics: DiagnosticFields;
  taskId?: string;
  constructor(
    message: string,
    code: string,
    status: number,
    diagnostics: DiagnosticFields = {},
    taskId?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.diagnostics = diagnostics;
    this.taskId = taskId;
  }
}
export async function api<T>(
  path: string,
  options: RequestInit & {
    onUploadProgress?: (percent: number, message: string) => void;
  } = {},
): Promise<T> {
  if (
    options.body instanceof FormData &&
    /^\/(projects\/[^/]+\/(upload|materials)|library\/folders\/[^/]+\/materials|viral\/analyze-upload)$/.test(
      path,
    )
  ) {
    const { directUpload } = await import("./directUpload");
    const upload = await directUpload<T>(
      path,
      options.body,
      options.onUploadProgress,
    );
    if (upload.handled) {
      workspaceCache.invalidate();
      return upload.result;
    }
  }
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");
  const requestId = headers.get("X-Request-ID") || crypto.randomUUID();
  headers.set("X-Request-ID", requestId);
  headers.set("X-AClip-Session", editingSession);
  const diagnostics: DiagnosticFields = {
    request_id: requestId,
    method: options.method || "GET",
    path: "/api/v1" + path,
  };
  let r: Response;
  const generation = sessionGeneration;
  let body: string;
  try {
    const { onUploadProgress: _progress, ...requestOptions } = options;
    r = await fetch("/api/v1" + path, {
      ...requestOptions,
      credentials: "same-origin",
      headers,
    });
    if (
      r.status === 401 &&
      generation === sessionGeneration &&
      !path.startsWith("/auth/")
    ) {
      window.dispatchEvent(new Event("aclip-auth-required"));
    }
    diagnostics.http_status = r.status;
    diagnostics.request_id = r.headers.get("X-Request-ID") || requestId;
    diagnostics.response_type =
      r.headers.get("content-type")?.split(";")[0] || "未声明";
    body = await r.text();
  } catch (error) {
    if (options.signal?.aborted) {
      if (options.signal.reason?.name === "TimeoutError")
        throw new ApiError(
          "请求超时，请稍后重试。",
          "REQUEST_TIMEOUT",
          0,
          diagnostics,
        );
      throw options.signal.reason || error;
    }
    diagnostics.http_status ??= 0;
    diagnostics.cause_type =
      error instanceof Error ? error.name : "NetworkError";
    throw new ApiError(
      "未能收到数据服务的完整响应，请检查连接。",
      "NETWORK_ERROR",
      diagnostics.http_status,
      diagnostics,
    );
  }
  if (
    r.ok &&
    !["GET", "HEAD"].includes((options.method || "GET").toUpperCase())
  )
    workspaceCache.invalidate(
      path.endsWith("/script-library/draft")
        ? (key) =>
            key.startsWith("/scripts") || key === "/projects?summary=true"
        : undefined,
    );
  if (r.status === 304 && headers.has("If-None-Match")) return undefined as T;
  if (r.status === 204) return undefined as T;
  if (
    r.headers.get("content-type")?.toLowerCase().includes("text/html") ||
    /^\s*</.test(body)
  ) {
    throw new ApiError(
      "未能连接到数据服务，网站返回了网页而非数据。请联系管理员检查后端部署及接口转发配置。",
      "API_RETURNED_HTML",
      r.status,
      diagnostics,
    );
  }
  let data;
  try {
    data = JSON.parse(body);
  } catch {
    throw new ApiError(
      `数据服务返回了${body.trim() ? "无法识别的数据" : "空响应"}（HTTP ${r.status}）。`,
      "INVALID_API_RESPONSE",
      r.status,
      diagnostics,
    );
  }
  if (!r.ok) {
    const message = apiErrorMessage(path, data);
    throw new ApiError(
      message,
      data?.error?.code || "UNKNOWN",
      r.status,
      { ...cleanDiagnostics(data?.error?.diagnostics), ...diagnostics },
      data?.task_id,
    );
  }
  return data;
}
export const post = <T>(path: string, body: unknown = {}) =>
  api<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Idempotency-Key": crypto.randomUUID() },
  });
export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: "PATCH", body: JSON.stringify(body) });
export function formatTime(us: number, precise = false) {
  const secs = us / 1e6;
  return `${Math.floor(secs / 60)
    .toString()
    .padStart(2, "0")}:${Math.floor(secs % 60)
    .toString()
    .padStart(2, "0")}${precise ? "." + (Math.floor(us / 100000) % 10) : ""}`;
}
export function formatFrame(us: number, fps: number) {
  const frame = Math.round((us / 1e6) * fps);
  return `${formatTime(Math.floor(frame / fps) * 1e6)}:${(frame % fps).toString().padStart(2, "0")}`;
}
export const versionName = (v: { type: string; number: number }) =>
  v.type === "original"
    ? "原始画面"
    : `${v.type === "composition" ? "视觉组合" : "AI 视频"} V${v.number}`;
